import { gql, NetworkStatus, useApolloClient, useQuery } from "@apollo/client";
import { useState } from "react";

import {
  convertStatusValueToFilter,
  USER_INTERACTION_EVENT,
} from "@zapier/reporting/src/constants";
import { RunStatus } from "@zapier/reporting/src/graphql-types";
import { HistoryFiltersTypes } from "@zapier/reporting/src/types";
import { getErrorMessage } from "@zapier/reporting/src/utils";
import { useEmitEvent } from "../useEmitEvent";
import { OnMassAction } from "../useMassAction";
import { ZapRunFragment, ZapRunsQuery } from "./useRuns.generated";

type UseTopZapsInput = {
  accountId?: string;
  filters: Partial<HistoryFiltersTypes>;
  getAppImages: boolean;
  getFolder?: boolean;
  onMassAction: OnMassAction;
  pageSize?: number;
  skip?: boolean;
};

const PAGE_SIZE = 10;

export const getRunsQuery = gql`
  fragment ZapRun on ZapRun {
    billableCount
    billingAlignedBillableCount
    billingAlignmentEnabled
    billingAlignmentFailed
    hasPaths
    heldReason
    heldStaleAuth
    id
    startTime
    status
    triggerType
    replayCount
    zap {
      id
      kind
      sourceUrl
      title
      folder @include(if: $getFolder) {
        id
        isShared
        title
      }
    }
    zapApps @include(if: $getAppImages) {
      id
      images {
        url32X32
      }
      name
    }
    zapOwner {
      id
      imageUrl
      name
    }
  }

  query ZapRuns(
    $accountId: ID!
    $apps: [String!]
    $channel: String
    $channelError: String
    $customuserIds: [ID!]
    $folderIds: [ID!]
    $getAppImages: Boolean!
    $getFolder: Boolean!
    $heldReasons: [HeldReason!]
    $heldStaleAuthIds: [String!]
    $limit: Int
    $offset: Int
    $periodEnd: String
    $periodStart: String
    $query: String
    $sortBy: String
    $status: [String!]
    $zapIds: [ID!]
  ) {
    zapRuns(
      accountId: $accountId
      apps: $apps
      channel: $channel
      channelError: $channelError
      customuserIds: $customuserIds
      folderIds: $folderIds
      heldReasons: $heldReasons
      heldStaleAuthIds: $heldStaleAuthIds
      limit: $limit
      offset: $offset
      periodEnd: $periodEnd
      periodStart: $periodStart
      query: $query
      sortBy: $sortBy
      status: $status
      zapIds: $zapIds
    ) {
      edges {
        ...ZapRun
      }
      totalActionable
      totalCount
    }
  }
`;

function enhanceRuns(
  runs: ZapRunFragment[],
  deletingRunIds: string[],
  replayingRunIds: string[]
) {
  return runs.map((run) => {
    if (deletingRunIds.includes(run.id)) {
      return {
        ...run,
        status: RunStatus.delete,
      };
    }
    if (replayingRunIds.includes(run.id)) {
      return {
        ...run,
        status: RunStatus.replay,
      };
    }
    return run;
  });
}

export function useRuns({
  accountId,
  filters,
  getAppImages,
  getFolder = false,
  onMassAction,
  pageSize = PAGE_SIZE,
  skip,
}: UseTopZapsInput) {
  const [deletingRunIds, setDeletingRunIds] = useState<string[]>([]);
  const [replayingRunIds, setReplayingRunIds] = useState<string[]>([]);
  const client = useApolloClient();
  const { emitEvent } = useEmitEvent();

  const transformedFilters = {
    apps: filters.apps,
    channel: filters.channel,
    channelError: filters.channelError,
    customuserIds: filters.ownerIds,
    folderIds: filters.folderIds,
    heldReasons: filters.heldReasons,
    heldStaleAuthIds: filters.heldStaleAuthIds,
    periodEnd: filters.dateFilter?.to,
    periodStart: filters.dateFilter?.from,
    query: filters.search,
    status: convertStatusValueToFilter(filters.status),
    zapIds: filters.nodeIds,
  };

  const variables = {
    accountId,
    getAppImages,
    getFolder,
    limit: pageSize,
    offset: filters.page ? (filters.page - 1) * pageSize : 0,
    sortBy: "-start_time",
    ...transformedFilters,
  };
  const shouldSkipQuery = !accountId || Boolean(skip);

  const queryResult = useQuery<ZapRunsQuery>(getRunsQuery, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: shouldSkipQuery,
    variables,
  });

  const totalActionable = queryResult.data?.zapRuns?.totalActionable;
  const runs = queryResult.data?.zapRuns?.edges || [];
  const enhancedRuns = enhanceRuns(runs, deletingRunIds, replayingRunIds);

  function onDelete(runIds: string[], isAllSelected: boolean) {
    const massActionVariables = {
      filters:
        isAllSelected && totalActionable
          ? {
              ...transformedFilters,
              limit: totalActionable,
              status: isAllSelected ? transformedFilters.status : null,
            }
          : transformedFilters,
      runIds: isAllSelected ? null : runIds,
    };

    onMassAction("delete", massActionVariables, (refetch = false) => {
      setReplayingRunIds([]);
      if (refetch) {
        // Refetch to update statuses
        queryResult.refetch();
      }
    });

    // Store run ids that are being deleted locally so we can show the
    // user that the runs are being deleted
    setDeletingRunIds(isAllSelected ? runs.map((run) => run.id) : runIds);
  }

  function onReplay(runIds: string[], isAllSelected: boolean) {
    const massActionVariables = {
      filters:
        isAllSelected && totalActionable
          ? {
              ...transformedFilters,
              limit: totalActionable,
              status: isAllSelected ? transformedFilters.status : null,
            }
          : transformedFilters,
      runIds: isAllSelected ? null : runIds,
    };

    emitEvent({
      eventName: USER_INTERACTION_EVENT,
      meta: {
        event_action: "click",
        interaction_goal: "troubleshoot_zap_run",
        interaction_name: "zap_runs_interaction",
        interaction_raw_text: `Replay ${runIds.length}`,
        option_selected: "replay",
      },
    });

    onMassAction("replay", massActionVariables, (refetch = false) => {
      setReplayingRunIds([]);
      if (refetch) {
        // Refetch to update statuses
        queryResult.refetch();
      }
    });

    // Store run ids that are being replayed locally so we can show the
    // user that the runs are being replaying
    setReplayingRunIds(isAllSelected ? runs.map((run) => run.id) : runIds);
  }

  function onRefetch() {
    if (accountId) {
      queryResult.refetch();
    }
  }

  /**
   * Since the cache relies on pagination, we cannot easily get all ZapRun
   * objects from the cache (without looping through each page the user has fetched)
   *
   * This function gets the raw runs out of the cache to allow us to do introspection
   * See https://github.com/zapier/zapier/pull/42757#discussion_r460851618 for more information
   */
  function getSelectedRuns(selectedIds: string[]) {
    const serializedCache = client.cache.extract();
    const selectedRuns = Object.values(serializedCache).filter(
      (item) => item.__typename === "ZapRun" && selectedIds.includes(item.id)
    );

    return selectedRuns;
  }

  // Ensures the full loading state is shown when the user changes filters and
  // there's nothing matching those filters in the cache
  const shouldIgnoreCache =
    shouldSkipQuery ||
    (queryResult.networkStatus === NetworkStatus.setVariables &&
      !queryResult.data);

  return {
    actionableRunCount: totalActionable || 0,
    error: getErrorMessage(queryResult.error),
    fetchMore: queryResult.fetchMore,
    getSelectedRuns,
    isLoading:
      queryResult.networkStatus === NetworkStatus.loading || shouldIgnoreCache,
    isRefetching:
      queryResult.networkStatus === NetworkStatus.refetch ||
      queryResult.networkStatus === NetworkStatus.setVariables,
    networkStatus: queryResult.networkStatus,
    onDelete,
    onRefetch,
    onReplay,
    runCount: queryResult.data?.zapRuns?.totalCount || 0,
    runs: enhancedRuns,
  };
}
